import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'

import { TransparentBgButton, SecondaryButton } from 'components/Shared/Button'
import { UploadIcon, CloseXIcon } from 'images'
import { useToast } from 'components/Shared/AppToast/ToastProvider'
import { updatePartnerBranding } from 'api/partner'
import { uploadBrandingLogo } from 'api/branding'

const MAX_LOGO_SIZE_BYTES = 5 * 1000 * 1000 // 5MB
const MIN_LOGO_DIMENSION_PX = 128

const readImageDimensions = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve({ width: img.naturalWidth, height: img.naturalHeight })
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('invalid image'))
    }
    img.src = url
  })

/**
 * "Business logo" field — Figma node 31504:25066/25089/25103 (fd-WebApp,
 * "Google business name - logo settings"). Own Cancel/Save pair, saved
 * independently of the business name field. Square, >=128px, <=5MB
 * PNG/JPG per the design's tag chips + helper copy.
 */
const BusinessLogoField = ({ initialValue, isLoading }) => {
  const { t } = useTranslation()
  const { addNotification } = useToast()
  const fileInputRef = useRef(null)

  const [logoUrl, setLogoUrl] = useState(initialValue)
  const [draftFile, setDraftFile] = useState(null)
  const [draftPreview, setDraftPreview] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    setLogoUrl(initialValue)
  }, [initialValue])

  const isEditingMode = isEditing || !logoUrl
  const displayedImage = draftPreview || logoUrl

  const handleEdit = () => setIsEditing(true)

  const handleCancel = () => {
    setDraftFile(null)
    setDraftPreview('')
    setLogoUrl(initialValue)
    setIsEditing(false)
  }

  const handleRemoveStaged = () => {
    setDraftFile(null)
    setDraftPreview('')
    setLogoUrl('')
  }

  const handleSelectFile = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return

    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      addNotification({ message: t('Settings.branding.logo.invalidType'), variant: 'error' })
      return
    }
    if (file.size > MAX_LOGO_SIZE_BYTES) {
      addNotification({ message: t('Settings.branding.logo.tooLarge'), variant: 'error' })
      return
    }

    try {
      const { width, height } = await readImageDimensions(file)
      if (width !== height) {
        addNotification({ message: t('Settings.branding.logo.notSquare'), variant: 'error' })
        return
      }
      if (width < MIN_LOGO_DIMENSION_PX) {
        addNotification({ message: t('Settings.branding.logo.tooSmall'), variant: 'error' })
        return
      }
    } catch (error) {
      addNotification({ message: t('Settings.branding.logo.invalidType'), variant: 'error' })
      return
    }

    setDraftFile(file)
    setDraftPreview(URL.createObjectURL(file))
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      let nextLogoUrl = logoUrl
      if (draftFile) {
        const uploadResult = await uploadBrandingLogo(draftFile)
        if (!uploadResult.success) {
          addNotification({ message: t('Settings.branding.logo.uploadError'), variant: 'error' })
          setIsSaving(false)
          return
        }
        nextLogoUrl = uploadResult.url
      }

      const saved = await updatePartnerBranding({ logoUrl: nextLogoUrl })
      setLogoUrl(saved.logoUrl || '')
      setDraftFile(null)
      setDraftPreview('')
      setIsEditing(false)
      addNotification({ message: t('Settings.branding.saveSuccess'), variant: 'success' })
    } catch (error) {
      addNotification({ message: t('Settings.branding.saveError'), variant: 'error' })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="settings-page__section">
      <div className="settings-page__sectionBody">
        <div className="settings-page__sectionState flex-grow-1">
          <h3 className="settings-page__sectionTitle">{t('Settings.branding.logo.title')}</h3>
          <p className="settings-page__sectionValue" style={{ marginBottom: '12px' }}>{t('Settings.branding.logo.description')}</p>
        </div>

        <div className="d-flex align-items-start gap-2">
          {isEditingMode && initialValue ? (
            <TransparentBgButton
              text={t('common.cancel')}
              onClick={handleCancel}
              color="navy"
              fullWidth={false}
              disabled={isSaving}
            />
          ) : null}
          {isEditingMode ? (
            <SecondaryButton
              text={t('common.save')}
              onClick={handleSave}
              color="navy"
              fullWidth={false}
              loading={isSaving}
              disabled={isLoading || !displayedImage}
            />
          ) : (
            <TransparentBgButton
              text={t('common.edit')}
              onClick={handleEdit}
              color="navy"
              fullWidth={false}
              disabled={isLoading}
            />
          )}
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg"
        onChange={handleSelectFile}
        style={{ display: 'none' }}
      />

      {displayedImage ? (
        <div
          className={`branding-logo-preview-wrapper position-relative${isEditingMode ? ' branding-logo-preview-wrapper--editable' : ''}`}
          style={{ width: 163 }}
          onClick={isEditingMode ? () => fileInputRef.current?.click() : undefined}
          role={isEditingMode ? 'button' : undefined}
          tabIndex={isEditingMode ? 0 : undefined}
        >
          <img src={displayedImage} alt={t('Settings.branding.logo.title')} className="branding-logo-preview" />
          {isEditingMode ? (
            <button
              type="button"
              className="branding-logo-remove-btn"
              onClick={(e) => {
                e.stopPropagation()
                handleRemoveStaged()
              }}
              aria-label={t('common.remove')}
            >
              <CloseXIcon width={9} height={9} />
            </button>
          ) : null}
        </div>
      ) : (
        <>
          <button type="button" className="branding-logo-dropzone" onClick={() => fileInputRef.current?.click()}>
            <span className="branding-logo-dropzone__icon">
              <UploadIcon width={20} height={20} />
            </span>
            <span className="branding-logo-dropzone__text">{t('Settings.branding.logo.uploadButton')}</span>
          </button>
          <div className="d-flex gap-2" style={{ marginTop: '12px' }}>
            <span className="branding-tag">{t('Settings.branding.logo.tagSquare')}</span>
            <span className="branding-tag">{t('Settings.branding.logo.tagMinSize')}</span>
            <span className="branding-tag">{t('Settings.branding.logo.tagMaxSize')}</span>
          </div>
        </>
      )}

      <p className="settings-page__sectionValue" style={{ marginTop: '12px' }}>{t('Settings.branding.logo.helperText')}</p>
    </div>
  )
}

BusinessLogoField.propTypes = {
  initialValue: PropTypes.string,
  isLoading: PropTypes.bool,
}

export default BusinessLogoField
